import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

/**
 * App-wide confirmation prompts for destructive actions.
 *
 * Deleting a workout, goal, plan, post or comment used to fire the moment the
 * trash icon was clicked — one stray tap and the record was gone with no undo
 * anywhere in the product. `confirm()` makes the guard a one-liner at each call
 * site, so there is no incentive to skip it:
 *
 *   if (!(await confirm({ title: 'Delete workout?' }))) return;
 *
 * It resolves true/false rather than taking callbacks so the calling code keeps
 * reading top-to-bottom.
 */
export interface ConfirmOptions {
  title: string;
  /** Say what is lost and whether it can be undone. */
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' (default) paints the action red for irreversible removals. */
  tone?: 'danger' | 'default';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | undefined>(undefined);

export const useConfirm = (): ConfirmFn => {
  const context = useContext(ConfirmContext);
  if (context === undefined) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  // Held in a ref so re-renders while the dialog is open don't drop the pending
  // promise's resolver.
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setOptions(opts);
    });
  }, []);

  const settle = useCallback((result: boolean) => {
    setOptions(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(result);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && (
        <ConfirmDialog
          {...options}
          onConfirm={() => settle(true)}
          onCancel={() => settle(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
};
